import React, { useCallback, useEffect, useRef, useState } from "react";
import Map, {
  Source,
  Layer,
  MapProvider,
  Popup,
  ScaleControl,
  NavigationControl,
} from "react-map-gl";
import pointsWithinPolygon from "@turf/points-within-polygon";

import PropTypes from "prop-types";
import {
  defaultLineStyle,
  internalCountStyle,
  labelStyle,
  relativeLabelStyle,
} from "./BaseMapStyles";
import DrawControl, { StyleControl } from "./controls/baseMapControls";
import WebMercatorViewport from "@math.gl/web-mercator";

import { setInfoFeedMessage } from "../../store/actions";
import { useDispatch } from "react-redux";

const map_access_token =
  "pk.eyJ1IjoiYWxleGdpbHNvbiIsImEiOiJjbG4wdDJ4eGMxcGcwMmlxdXNwa3A1ZXc5In0.dINhFz-zHChgqP0LCjgK9w";

const applyToArray = (func, array) => func.apply(Math, array);

export const getDefaultBounds = (sources) => {
  const visibleFeatures = sources.reduce((agg, cur) => {
    const features = cur?.data?.features;

    return features
      ? [...agg, ...(features[0]?.geometry?.type === "Point" ? features : [])]
      : [...agg];
  }, []);

  if (visibleFeatures.length) {
    const pointsLng = visibleFeatures.map(
      (feature) => feature.geometry.coordinates[0]
    );
    const pointsLat = visibleFeatures.map(
      (feature) => feature.geometry.coordinates[1]
    );

    const cornersLngLat = [
      [applyToArray(Math.min, pointsLng), applyToArray(Math.min, pointsLat)],
      [applyToArray(Math.max, pointsLng), applyToArray(Math.max, pointsLat)],
    ];

    const viewport = new WebMercatorViewport({
      width: 800,
      height: 600,
    }).fitBounds(cornersLngLat, {
      padding: 50,
    });

    const { longitude, latitude, zoom } = viewport;
    return { longitude, latitude, zoom };
  }
  return { longitude: -100, latitude: 40, zoom: 3, boundsErr: true };
};

export const setBoundsToSources = (sources, map) => {
  if (!map) {
    console.error(
      "Unable to set bounds: Basemap object required as second arg, setBoundsToSources"
    );
    return;
  }
  const bounds = getDefaultBounds(sources);
  //returns the map object
  if (!bounds.boundsErr) {
    return map.current
      .panTo([bounds.longitude, bounds.latitude], { animate: false })
      .zoomTo(bounds.zoom, { animate: false });
  }
};

const getFeaturesWithinScreen = (bounds, sources, map) => {
  const boundsPoly = {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [bounds._ne.lng, bounds._ne.lat],
          [bounds._ne.lng, bounds._sw.lat],
          [bounds._sw.lng, bounds._sw.lat],
          [bounds._sw.lng, bounds._ne.lat],
          [bounds._ne.lng, bounds._ne.lat],
        ],
      ],
    },
  };

  const selectableSources = sources.filter(
    (source) => source.style.type === "circle"
  );

  const toSearch = {
    type: "FeatureCollection",
    features: selectableSources.reduce((agg, cur) => {
      const selectablePoints = map.current.getSource(cur.id)._data.features;
      return [...agg, ...(selectablePoints || [])];
    }, []),
  };

  return pointsWithinPolygon(toSearch, boundsPoly).features.map((d) => d.id);
};

export default function BaseMap(props) {
  const [viewState, setViewState] = useState({ longitude: -100, latitude: 40 });
  const [hoveredFeatures, setHoveredFeatures] = useState([]);
  const [popupFeature, setPopupFeature] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [mousePos, setMousePos] = useState(null);
  const [isSat, setIsSat] = useState(false);

  const mapRef = useRef();

  const dispatch = useDispatch();

  // feature -> source
  const getFeatureSource = useCallback(
    (feature) => {
      return props.sources.find((source) => source.id === feature?.source);
    },
    [props.sources]
  );

  // feature -> nothing
  // handles a user moving their mouse out of a feature
  // side effects:
  // - updates features style to return to its unhovered state
  // - calls onEndHover with the feature, sets
  // - sets hoveredFeatures to []
  const handleEndHover = useCallback(
    (features) => {
      const map = mapRef.current?.getMap();

      if (map) {
        features.forEach((feature) => {
          map.setFeatureState(
            { source: feature.source, id: feature.id },
            { hover: false }
          );
        });

        const unhoveredFeatureSource = getFeatureSource(features[0]);
        unhoveredFeatureSource &&
          unhoveredFeatureSource.onEndHover &&
          unhoveredFeatureSource.onEndHover(features);

        setHoveredFeatures([]);
      }
    },
    [setHoveredFeatures, getFeatureSource]
  );

  const parsePropertyData = useCallback((feature) => {
    if (typeof feature.properties.data === "string") {
      let fixedFeature = { ...feature };
      fixedFeature.properties.data = JSON.parse(fixedFeature.properties.data);
      return fixedFeature;
    }
    return feature;
  }, []);

  // feature -> nothing
  // handles a user moving there mouse into and inside of a feature
  // side effects:
  // - if the feature id changes, end hover is called
  // - the style of the hovered point is changed
  // - calls the sources onHover function with the feature
  // - sets hoveredFeature to the passed feature
  const handleHover = useCallback(
    (features) => {
      if (
        (hoveredFeatures.length &&
          hoveredFeatures.some((feature) => {
            return !features.find((n) => feature.id === n.id);
          })) ||
        hoveredFeatures.length !== features.length
      ) {
        handleEndHover(hoveredFeatures);
      } else if (hoveredFeatures.length) {
        setPopupFeature({
          ...features[0],
          geometry: { ...features[0]?.geometry },
        });
        return;
      }

      const map = mapRef.current?.getMap();

      if (map?.isStyleLoaded()) {
        features.forEach((feature) => {
          map.setFeatureState(
            { source: feature.source, id: feature.id },
            { hover: true }
          );
        });

        const hoveredFeatureSource = getFeatureSource(features[0]);

        hoveredFeatureSource &&
          hoveredFeatureSource.onHover &&
          hoveredFeatureSource.onHover(
            [...features].map((feature) => parsePropertyData(feature)),
            mapRef
          );

        setHoveredFeatures(
          features.map((feature) => {
            return { ...feature, geometry: { ...feature.geometry } };
          })
        );

        setPopupFeature({
          ...features[0],
          geometry: { ...features[0]?.geometry },
        });
      }
    },
    [handleEndHover, hoveredFeatures, getFeatureSource, parsePropertyData]
  );

  useEffect(() => {
    if (props.hoveredFeatures?.length && props.hoveredSource) {
      const source = props.sources.find(
        (source) => source.id === props.hoveredSource
      );
      const features = source?.data.features.filter(
        (feature) => props.hoveredFeatures.indexOf(feature.id) > -1
      );

      if (
        features &&
        (!features.find((n) => n.id === hoveredFeatures[0]?.id) ||
          features.length !== hoveredFeatures.length)
      ) {
        handleHover(
          features.map((feature) => {
            return { ...feature, source: props.hoveredSource };
          })
        );
      }
    } else if (hoveredFeatures?.length) {
      handleEndHover(hoveredFeatures);
    }
    const newSelectedFeatures = props.sources.reduce((agg, cur) => {
      return [...agg, ...(cur.selectedFeatures || [])];
    }, []);

    if (newSelectedFeatures.length !== selectedFeatures.length) {
      handleSelectedUpdates(newSelectedFeatures);
    }
  }, [props.hoveredFeatures, props.hoveredSource, props.sources]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseMove = useCallback(
    (e) => {
      if (!mousePos || !hoveredFeatures.length) {
        setMousePos(e.lngLat);
      }

      // the mouse isn't being moved on the map when it is inside a popup, so we know that we
      // can use this event as a way to tell if the user is hovering a popup, a feature that is lacking
      // in mapbox gl
      e.features.length ? handleHover([e.features[0]]) : setPopupFeature(null);
    },
    [handleHover, setPopupFeature] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSelectedUpdates = useCallback(
    (features) => {
      const currentMap = mapRef.current.getMap();

      features.forEach((feature) => {
        currentMap.setFeatureState(
          { source: feature.source, id: feature.id },
          { selected: true }
        );
      });

      const difference = selectedFeatures.filter(
        (feature) => !features.find((n) => n.id === feature.id)
      );

      difference.forEach((feature) => {
        currentMap.setFeatureState(
          { source: feature.source, id: feature.id },
          { selected: false }
        );
      });

      setSelectedFeatures(features);
    },
    [selectedFeatures]
  );

  const handleDblClick = useCallback(
    (feature) => {
      const featureSource = getFeatureSource(feature);
      const hasDblClickEvent = featureSource?.onDblClick;
      const is_disabled = !!feature?.properties?.disabled;

      hasDblClickEvent && !is_disabled && featureSource.onDblClick(feature);
    },
    [getFeatureSource]
  );

  const handleClick = useCallback(
    (feature) => {
      const clickedFeatureSource = getFeatureSource(feature);
      const hasClickEvent = clickedFeatureSource?.onClick;
      const is_disabled = !!feature?.properties?.disabled;

      hasClickEvent && !is_disabled && clickedFeatureSource.onClick(feature);
    },
    [getFeatureSource]
  );

  const checkForEmptyPopup = (body) => {
    if (!body) {
      setPopupFeature(null);
    } else {
      return body;
    }
  };

  const getPopupLng = (feature) => {
    if (feature.geometry.type === "Point") {
      return feature.geometry.coordinates[0];
    } else if (feature.geometry.type === "Polygon") {
      return props.hoveredSource
        ? feature.geometry.coordinates[0][0][0]
        : mousePos?.lng || null;
    } else if (feature.geometry.type === "LineString") {
      return 0;
    }
  };

  const getPopupLat = (feature) => {
    if (feature.geometry.type === "Point") {
      return feature.geometry.coordinates[1];
    } else if (feature.geometry.type === "Polygon") {
      return props.hoveredSource
        ? feature.geometry.coordinates[0][0][1]
        : mousePos?.lat || null;
    } else if (feature.geometry.type === "LineString") {
      return 0;
    }
  };

  return (
    <MapProvider>
      <Map
        {...viewState}
        dragRotate={false}
        dragPan={false}
        ref={mapRef}
        minZoom={3}
        onLoad={(e) => {
          setViewState(getDefaultBounds(props.sources.filter((n) => n)));
          const bounds = e.target.getBounds();
          const pointsWithinScreen = getFeaturesWithinScreen(
            bounds,
            props.sources,
            mapRef
          );
          const icons = props.requiredIcons || [];

          icons.forEach((symbol) => {
            mapRef.current
              .getMap()
              .loadImage(symbol.iconSrc, (error, image) => {
                if (error) {
                  throw error;
                }
                mapRef.current
                  .getMap()
                  .addImage(symbol.iconName, image, { sdf: true });
              });
          });

          dispatch(
            setInfoFeedMessage(
              'Click the pencil or tap the "3" key to enter draw mode.\n\n You can use the "esc" key to exit the mode.'
            )
          );

          props.handleViewChange(
            {
              ...viewState,
              bounds: bounds,
              visiblePoints: pointsWithinScreen,
            },
            mapRef
          );
        }}
        onMove={(e) => {
          setViewState(e.viewState);
        }}
        onMoveEnd={(e) => {
          const bounds = e.target.getBounds();
          const pointsWithinScreen = getFeaturesWithinScreen(
            bounds,
            props.sources,
            mapRef
          );

          props.handleViewChange({
            ...e.viewState,
            bounds: bounds,
            visiblePoints: pointsWithinScreen,
          });
        }}
        mapboxAccessToken={map_access_token}
        style={{ width: "100%", height: "100%" }}
        mapStyle={
          isSat
            ? "mapbox://styles/mapbox/satellite-v9"
            : "mapbox://styles/mapbox/light-v10"
        }
        onMouseMove={(e) => handleMouseMove(e)}
        onMouseLeave={(e) => {
          e.features.length && handleEndHover([e.features[0]]);
        }}
        onClick={(e) => e.features.length && handleClick(e.features[0])}
        onDblClick={(e) => e.features.length && handleDblClick(e.features[0])}
        interactiveLayerIds={props.sources
          .filter((source) => source && source.isInteractive)
          .map((source) => source.style.id)}
      >
        {props.sources.map((source) => {
          const labelId = "label-" + source.id;
          return (
            <Source
              key={source.id}
              id={source.id}
              type={source.type}
              data={
                source.hidden
                  ? { type: "FeatureCollection", features: [] }
                  : source.data
              }
              // lineMetrics={!!source.lineMetrics}
              lineMetrics={true}
            >
              <Layer {...source.style} />
              {source.outline && <Layer {...defaultLineStyle} />}
              {source.internalCount ? (
                <Layer {...relativeLabelStyle} />
              ) : (
                <Layer {...labelStyle} id={labelId} />
              )}
              {source.internalCount && <Layer {...internalCountStyle} />}
              {popupFeature && popupFeature.source === source.id && (
                <Popup
                  anchor="top"
                  className="popup"
                  longitude={getPopupLng(popupFeature)}
                  latitude={getPopupLat(popupFeature)}
                  closeButton={false}
                  onClose={() => setPopupFeature(null)}
                >
                  {checkForEmptyPopup(
                    source.popupComponentGenerator(
                      parsePropertyData({ ...popupFeature })
                    )
                  )}
                </Popup>
              )}
            </Source>
          );
        })}
        {!props.static && (
          <>
            <ScaleControl />
            {/* {!props.disableDraw && ( */}
            <DrawControl
              position="bottom-left"
              displayControlsDefault={false}
              controls={{ polygon: true }}
              defaultMode={"simple_select"}
              onCreate={(poly, draw) => {
                const searchWithin = {
                  type: "Feature",
                  geometry: {
                    type: "Polygon",
                    coordinates: [poly.features[0].geometry.coordinates[0]],
                  },
                };

                const selectableSources = props.sources.filter(
                  (source) =>
                    source.selectedFeatures && source.style.type === "circle"
                );

                const toSearch = {
                  type: "FeatureCollection",
                  features: selectableSources.reduce((agg, cur) => {
                    const selectablePoints = mapRef.current.getSource(cur.id)
                      ._data.features;
                    return [
                      ...agg,
                      ...(selectablePoints || []).filter(
                        (d) => !d.properties?.disabled
                      ),
                    ];
                  }, []),
                };

                const selectedPoints = pointsWithinPolygon(
                  toSearch,
                  searchWithin
                );

                selectedPoints.features.forEach((point) => {
                  // use if you don't want to flipflop points that have already been selected
                  // const isSelected = mapRef.current.getFeatureState({
                  //   id: point.id,
                  //   source: point.source,
                  // }).selected;

                  handleClick(point);
                });
              }}
            />
            {/* )} */}
            <NavigationControl position="bottom-left" showCompass={false} />
            <StyleControl
              position="bottom-left"
              onUpdate={() => {
                setIsSat((prevSat) => !prevSat);
              }}
            />
          </>
        )}
      </Map>
    </MapProvider>
  );
}

BaseMap.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.object).isRequired,
  hoveredFeatures: PropTypes.arrayOf(PropTypes.number),
  hoveredSource: PropTypes.string,
  handleViewChange: PropTypes.func,
  static: PropTypes.bool,
  disableDraw: PropTypes.bool,
  requiredIcons: PropTypes.arrayOf(PropTypes.object),
};

BaseMap.defaultProps = {
  sources: [],
  handleViewChange: () => {},
};
